import React from "react";
import PropTypes from "prop-types";
import PageContainer from "./PageContainer";
import DataTable from "./DataTable";
import Pagination from "./Pagination";
import EmptyState from "./EmptyState";
import TableSkeleton from "./TableSkeleton";

// The list/loading/error/empty/paginate shell every pharmacy page shares.
//
// All four pages are the same shape — a heading, a filter row, a table, a pager — and
// differ only in their columns, their query and their filters. Writing that shell once
// keeps the loading and empty states identical across the console instead of four
// slightly different takes on "no rows", which is what happened on the ops side.
//
// `query` is a react-query result; this reads only `isLoading`/`isIdle`/`isError`, so a
// page can pass any hook that follows that contract.
function ListPage({ title, subtitle, actions, filters, columns, rows, rowClassName, query, pagination, onPageChange, emptyTitle, emptyHint, testId }) {
    const isLoading = query.isLoading || query.isIdle;

    return (
        <PageContainer fullBleed>
            <header className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-[22px] font-bold text-[var(--ops-carbon-900)]">{title}</h1>
                    {subtitle && (
                        <p className="mt-1 max-w-[560px] text-sm text-[var(--ops-carbon-500)]">{subtitle}</p>
                    )}
                </div>
                {actions}
            </header>

            {filters}

            {isLoading ? (
                <div
                    data-testid={`${testId}-loading`}
                    className="w-full overflow-x-auto rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]"
                >
                    <table className="w-full min-w-full border-collapse text-sm">
                        <tbody>
                            <TableSkeleton rows={10} cols={Math.min(columns.length, 8)} />
                        </tbody>
                    </table>
                </div>
            ) : query.isError ? (
                <div
                    data-testid={`${testId}-error`}
                    className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]"
                >
                    <EmptyState
                        title="Couldn't load this list"
                        hint="Something went wrong reaching the service. Try again in a moment."
                    />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState title={emptyTitle} hint={emptyHint} />
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <DataTable columns={columns} rows={rows} rowClassName={rowClassName} />
                    {pagination && (
                        <Pagination
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onChange={onPageChange}
                        />
                    )}
                </div>
            )}
        </PageContainer>
    );
}

ListPage.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    actions: PropTypes.node,
    filters: PropTypes.node,
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array,
    /** Passed through to DataTable — per-row class hook, e.g. status tinting. */
    rowClassName: PropTypes.func,
    query: PropTypes.shape({
        isLoading: PropTypes.bool,
        isIdle: PropTypes.bool,
        isError: PropTypes.bool,
    }).isRequired,
    pagination: PropTypes.shape({
        page: PropTypes.number,
        size: PropTypes.number,
        total: PropTypes.number,
    }),
    onPageChange: PropTypes.func,
    emptyTitle: PropTypes.string,
    emptyHint: PropTypes.string,
    testId: PropTypes.string.isRequired,
};

ListPage.defaultProps = {
    subtitle: null,
    actions: null,
    filters: null,
    rows: [],
    rowClassName: null,
    pagination: null,
    onPageChange: () => {},
    emptyTitle: "Nothing here yet",
    emptyHint: "Try a different filter.",
};

export default ListPage;
